import { Typography } from '@neo/primitives/typography'
import {
  Bot,
  Brain,
  BrainCircuit,
  Headset,
  Mic,
  MicOff,
  Play,
  SkipForward,
  Split,
  User,
  Wrench,
} from 'lucide-react'
import { humanizeSkipReason } from '../analysis/utils'
import { TimeWithMillis } from '../analysis-v2/utils'
import type { SystemChunkMetadata, SystemChunkType } from '../types'
import { isTextAgentChannel } from '../types'
import { getSkillStartedDetail } from '../utils'
import { DurationRenderer } from './duration'

interface CallEventProps {
  timestamp: string
  type: SystemChunkType
  text?: string
  result?: React.ReactNode
  firstEventTimestamp?: string
  metadata?: SystemChunkMetadata | null
  toolName?: string
  toolInput?: Record<string, unknown> | null
  toolResponse?: string | null
  toolCallError?: boolean
  time?: number | null
  // The node's channel. Only chat opts into the intent-route rendering below;
  // absent or any other channel keeps today's behaviour exactly.
  channel?: string | null
}

// Human detail for chunk types whose payload matters in the transcript; the
// bare label alone ("Variable skipped") hides which one and why.
const getEventDetail = (type: SystemChunkType, metadata?: SystemChunkMetadata | null) => {
  if (!metadata) return null
  if (type === 'TASK_STARTED') return getSkillStartedDetail(metadata)
  if (type === 'VARIABLE_SKIPPED') {
    if (!metadata.variable) return null
    const reason = humanizeSkipReason(metadata.reason)
    return reason ? `${metadata.variable} · ${reason}` : metadata.variable
  }
  return null
}

/**
 * A chat intent is implemented as a `handoff` tool call, but it is a routing
 * decision rather than a tool the agent invoked. Chat-gated on purpose: an action
 * name is customer-authored, so a voice flow may legitimately have a tool called
 * `handoff` and must keep rendering exactly as it does today.
 */
const isChatIntentRoute = (type: SystemChunkType, toolName?: string, channel?: string | null) =>
  isTextAgentChannel(channel) && type === 'TOOL_CALL_EVENT' && toolName === 'handoff'

/**
 * A human agent arriving on or leaving a chat handoff. These carry a written sentence, which the
 * default branch drops in favour of a mic icon and a label humanised from the enum.
 */
const isHandoffPresence = (type: SystemChunkType, channel?: string | null) =>
  isTextAgentChannel(channel) && (type === 'AGENT_ASSIGNED' || type === 'AGENT_LEFT')

const getEventIcon = (type: SystemChunkType, isIntentRoute: boolean, isPresence: boolean) => {
  if (isIntentRoute) {
    return <Split className="h-3 w-3 text-purple-600" />
  }
  // Gated rather than a case below: adding these types to the enum moved them off the default
  // branch for every channel, which would give a voice event a headset instead of its mic.
  if (isPresence) {
    return <Headset className="h-3 w-3 text-cyan-600" />
  }
  switch (type) {
    case 'BOT_STARTED_SPEAKING':
      return <Bot className="h-3 w-3 text-orange-600" />
    case 'BOT_STOPPED_SPEAKING':
      return <Bot className="h-3 w-3 text-orange-600" />
    case 'USER_STARTED_SPEAKING':
      return <User className="h-3 w-3 text-cyan-600" />
    case 'USER_STOPPED_SPEAKING':
      return <User className="h-3 w-3 text-cyan-600" />
    case 'MODEL_STARTED_EVENT':
      return <Brain className="h-3 w-3 text-purple-600" />
    case 'TOOL_CALL_EVENT':
    case 'KNOWLEDGE_BASE_SEARCH_EVENT':
      return <Wrench className="h-3 w-3 text-green-600" />
    case 'USER_MIGHT_STILL_BE_SPEAKING':
      return <User className="h-3 w-3 text-yellow-600" />
    case 'SKILL_NODE_EXECUTED':
      return <BrainCircuit className="h-3 w-3 text-green-600" />
    case 'SPEECH_DETECTED':
      return <Mic className="h-3 w-3 text-red-600" />
    case 'FILLER_DROPPED':
      return <MicOff className="h-3 w-3 text-yellow-600" />
    case 'VARIABLE_SKIPPED':
      return <SkipForward className="h-3 w-3 text-secondary" />
    case 'TASK_STARTED':
      return <Play className="h-3 w-3 text-blue-600" />
    case 'AGENT_RESPONSE':
      return <Bot className="h-3 w-3 text-orange-600" />
    case 'USER_RESPONSE':
      return <User className="h-3 w-3 text-cyan-600" />
    default:
      return <Mic className="h-3 w-3 text-st2" />
  }
}

const getEventLabel = (type: SystemChunkType) => {
  switch (type) {
    case 'BOT_STARTED_SPEAKING':
      return 'Agent started speaking'
    case 'AGENT_RESPONSE':
      return 'Agent response generated'
    case 'USER_RESPONSE':
      return 'User response'
    case 'BOT_STOPPED_SPEAKING':
      return 'Agent speech ended'
    case 'USER_STARTED_SPEAKING':
      return 'User speech detected'
    case 'USER_STOPPED_SPEAKING':
      return 'User speech ended'
    case 'MODEL_STARTED_EVENT':
      return 'Model processing started'
    case 'TOOL_CALL_EVENT':
      return 'Tool call executed'
    case 'KNOWLEDGE_BASE_SEARCH_EVENT':
      return 'Knowledge base search executed'
    case 'USER_MIGHT_STILL_BE_SPEAKING':
      return 'User might still be speaking'
    case 'SKILL_NODE_EXECUTED':
      return 'Skill node executed'
    case 'SPEECH_DETECTED':
      return 'Barge-in: speech confirmed'
    case 'FILLER_DROPPED':
      return 'Barge-in: filler dropped'
    case 'VARIABLE_SKIPPED':
      return 'Variable skipped'
    case 'TASK_STARTED':
      return 'Skill started'
    default:
      return String(type).toLowerCase().replace(/_/g, ' ')
  }
}

// An AI-node handoff is a routing decision the flow author reasons about as an
// intent, but the runtime implements it as a `handoff` tool call. Name the route
// it took ("Routed via intent: billing") instead of the generic "Tool call
// executed". Every other event keeps its label.
const getEventHeaderLabel = (
  type: SystemChunkType,
  isIntentRoute: boolean,
  toolInput?: Record<string, unknown> | null,
  presenceText?: string,
): string => {
  if (isIntentRoute) {
    const intent = typeof toolInput?.intent === 'string' ? toolInput.intent : undefined
    return intent ? `Routed via intent: ${intent}` : 'Handoff'
  }
  // The signal's own sentence beats the humanised enum name, so it IS the label.
  if (presenceText) return presenceText
  return getEventLabel(type)
}

export const SystemEvent = ({
  timestamp,
  type,
  text,
  result,
  firstEventTimestamp,
  metadata,
  toolName,
  toolInput,
  toolResponse,
  toolCallError,
  time,
  channel,
}: CallEventProps) => {
  const hasText = text && (type === 'BOT_STARTED_SPEAKING' || type === 'USER_STARTED_SPEAKING')
  const detail = getEventDetail(type, metadata)
  // A chat intent carries no input, response or duration — the tool card would
  // show an empty shell that reads as a failed call.
  const isIntentRoute = isChatIntentRoute(type, toolName, channel)
  const isPresence = isHandoffPresence(type, channel)
  // The expandable tool card exists for chat transcripts. Voice never had one and
  // must not gain one, so it is gated on the channel rather than the event type.
  const isTool =
    isTextAgentChannel(channel) &&
    (type === 'TOOL_CALL_EVENT' || type === 'KNOWLEDGE_BASE_SEARCH_EVENT') &&
    !isIntentRoute

  return (
    <div className={`rounded-xl p-3 ${hasText ? 'bg-transparent' : ''}`}>
      <div className="flex gap-6 items-center">
        {/* Timestamp */}
        <div className="min-w-[80px] text-right flex-shrink-0">
          <Typography variant="XS/400" className="text-secondary">
            <TimeWithMillis time={timestamp} />
          </Typography>
          {firstEventTimestamp ? (
            <DurationRenderer timestamp={timestamp} firstEventTimestamp={firstEventTimestamp} />
          ) : null}
        </div>

        {/* Event Type */}
        <div className="flex items-center gap-1 bg-hv1 rounded px-2 py-1 w-fit">
          <div className="bg-orange-100 rounded p-1">
            {getEventIcon(type, isIntentRoute, isPresence)}
          </div>
          <div className="flex gap-1 items-center">
            <Typography variant="XS/400" className="text-secondary truncate">
              {getEventHeaderLabel(type, isIntentRoute, toolInput, isPresence ? text : undefined)}
            </Typography>
            {detail && (
              <Typography variant="XS/500" className="text-foreground truncate">
                {detail}
              </Typography>
            )}
            {result}
          </div>
        </div>
      </div>

      {/* Tool detail — name, status, input, response, duration. Expandable so a
          long response never dominates the transcript. Non-tool events unchanged. */}
      {isTool ? (
        <details className="mt-2 ml-[104px] rounded-lg border border-st1 p-3">
          <summary className="cursor-pointer">
            <Typography variant="XS/500" className="text-primary">
              {toolName || getEventLabel(type)}
            </Typography>
          </summary>
          <div className="mt-2 flex flex-col gap-1">
            <Typography variant="XS/400" className="text-secondary">
              Status: {toolCallError ? 'Failed' : 'Succeeded'}
            </Typography>
            <pre className="whitespace-pre-wrap break-words text-xs-400 text-secondary">
              Input: {JSON.stringify(toolInput ?? {}, null, 2)}
            </pre>
            <pre className="whitespace-pre-wrap break-words text-xs-400 text-secondary">
              Response: {toolResponse ?? '—'}
            </pre>
            <Typography variant="XS/400" className="text-secondary">
              Duration: {time == null ? '—' : `${time.toFixed(2)}s`}
            </Typography>
          </div>
        </details>
      ) : null}
    </div>
  )
}
